import { useState } from 'react';
import { Strings } from '@/constants/strings';
import { FindRiskGauge } from '@/components/FindRiskGauge';
import { RecommendationsModal } from '@/components/RecommendationsModal';
import type { FindRiskResult as FindRiskResultType, FindRiskCategory } from '@/data/FindRisk';
import type { RiskTier } from '@/components/RecommendationsCard';

interface FindRiskResultProps {
  result: FindRiskResultType;
  userMode: 'healthcare' | 'public';
  onNewCalculation: () => void;
}

function getFindRiskRecommendations(category: FindRiskCategory) {
  const content: Record<
    FindRiskCategory,
    { tier: RiskTier; title: string; icon: string; bullets: string[] }
  > = {
    low: { tier: 'green', title: Strings.findRiskLow, icon: '✅', bullets: [Strings.findRiskRecLow] },
    mild: {
      tier: 'green',
      title: Strings.findRiskMild,
      icon: '✅',
      bullets: [Strings.findRiskRecMild],
    },
    moderate: {
      tier: 'orange',
      title: Strings.findRiskModerate,
      icon: '⚠️',
      bullets: [Strings.findRiskRecModerate],
    },
    high: {
      tier: 'orange',
      title: Strings.findRiskHigh,
      icon: '⚠️',
      bullets: [Strings.findRiskRecHigh],
    },
    veryHigh: {
      tier: 'red',
      title: Strings.findRiskVeryHigh,
      icon: '🚨',
      bullets: [Strings.findRiskRecHigh],
    },
  };
  return content[category];
}

export function FindRiskResultView({ result, userMode, onNewCalculation }: FindRiskResultProps) {
  const [recommendationsModalOpen, setRecommendationsModalOpen] = useState(false);
  const isPublic = userMode === 'public';
  const recommendations = getFindRiskRecommendations(result.category);

  return (
    <div className="flex min-h-[calc(100vh-56px)] flex-1 flex-col bg-surface">
      <div className="flex-1 overflow-y-auto px-6 py-6">
        <div className="mx-auto flex max-w-md flex-col space-y-6">
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium leading-relaxed text-amber-900">
            {Strings.medicalDisclaimer}
          </div>

          <div className="overflow-hidden rounded-3xl border border-slate-100 bg-white/80 py-8 px-6 shadow-sm backdrop-blur-md">
            <h2 className="mb-4 text-center text-xl font-semibold text-text-heading">
              {Strings.findRiskYourResult}
            </h2>
            <p className="mb-2 text-center text-4xl font-bold text-primary">
              {Strings.findRiskScore}: {result.score}
            </p>
            <div className="flex flex-col items-center py-2">
              <FindRiskGauge score={result.score} category={result.category} compact />
            </div>
            {isPublic ? (
              <div className="mt-4 flex justify-center">
                <button
                  type="button"
                  onClick={() => setRecommendationsModalOpen(true)}
                  className={`rounded-full px-8 py-3 text-base font-bold text-[#FFFFFF] transition focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1A1A1A]/20 ${
                    result.category === 'low' || result.category === 'mild'
                      ? 'bg-risk-low hover:opacity-90 active:opacity-95'
                      : result.category === 'moderate'
                        ? 'bg-risk-low hover:opacity-90 active:opacity-95'
                        : 'bg-risk-very-high hover:opacity-90 active:opacity-95'
                  }`}
                  aria-label="Sağlık önerilerini göster"
                >
                  Önerileri Gör
                </button>
              </div>
            ) : null}
          </div>

          <p className="rounded-xl bg-card-bg px-4 py-3 text-sm leading-relaxed text-text-muted">
            {Strings.clinicalResultFooter}
          </p>

          <button
            type="button"
            onClick={onNewCalculation}
            className="w-full rounded-2xl bg-primary py-4 text-lg font-bold text-white transition hover:bg-primary-active active:bg-primary-active"
          >
            {Strings.findRiskNewCalculation}
          </button>
        </div>
      </div>

      <RecommendationsModal
        visible={recommendationsModalOpen}
        onClose={() => setRecommendationsModalOpen(false)}
        tier={recommendations.tier}
        title={recommendations.title}
        icon={recommendations.icon}
        bullets={recommendations.bullets}
      />
    </div>
  );
}
